import * as React from 'react';
import { createPortal } from 'react-dom';
import { storyblokEditable } from 'gatsby-source-storyblok';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { faBadgeCheck, faMagnifyingGlass, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { render, NODE_PARAGRAPH, MARK_LINK } from 'storyblok-rich-text-react-renderer';
import LinkResolver from '../../helpers/link-resolver';
import { getStoryblokAlt } from '../../helpers/common';

export const AUTHOR_BOX_ID = 'author';
export const REVIEWER_BOX_ID = 'reviewer';

const LABELS = {
  de: {
    author: 'Autor',
    reviewer: 'Geprüft von',
    certificates: 'Zertifikate',
    close: 'Schließen',
    enlarge: 'Zertifikat vergrößern',
  },
  en: {
    author: 'Author',
    reviewer: 'Reviewed by',
    certificates: 'Certificates',
    close: 'Close',
    enlarge: 'Enlarge certificate',
  },
};

const getLinkUrl = (link) => {
  if (!link) return '';
  if (typeof link === 'string') return link;
  return link.url || link.cached_url || '';
};

/**
 * Returns true when the resolved author story has enough content to render a box.
 * Authors without bio and image (e.g. "Latori GmbH") only show up in the post meta row.
 */
export const hasAuthorBox = (author) => {
  const content = author?.content;
  if (!author?.name || !content) return false;
  return Boolean(content.bio || content.image?.filename);
};

function AuthorImage({ image, name }) {
  if (!image?.filename) return null;
  const { filename } = image;
  const isSvg = filename.includes('.svg');

  return (
    <picture className="shrink-0 self-center md:self-start">
      {!isSvg && (
        <source
          type="image/avif"
          srcSet={`${filename}/m/136x136/smart/filters:format(avif) 1x, ${filename}/m/272x272/smart/filters:format(avif) 2x`}
        />
      )}
      <img
        alt={getStoryblokAlt(image, name)}
        className="!m-0 h-[136px] w-[136px] rounded-full object-cover"
        src={isSvg ? filename : `${filename}/m/272x272/smart/filters:format(webp)`}
        width={136}
        height={136}
        loading="lazy"
      />
    </picture>
  );
}

function CertificateImage({ image, title }) {
  if (!image?.filename) return <div className="w-9 shrink-0" />;
  const { filename } = image;
  const isSvg = filename.includes('.svg');

  return (
    <img
      alt={getStoryblokAlt(image, title || '')}
      className="!m-0 w-9 h-auto shrink-0 object-contain"
      src={isSvg ? filename : `${filename}/m/72x0/filters:format(webp)`}
      width={36}
      loading="lazy"
    />
  );
}

// Story links resolve to the linked story's slug, everything else uses the cached url
const getCertificateLinkUrl = (link) => {
  if (!link) return null;
  if (link?.story?.full_slug) return `/${link.story.full_slug}`;
  return `/${link?.cached_url}` || null;
};

function CertificateRow({ certificate, enlargeLabel, onOpen }) {
  const linkUrl = getCertificateLinkUrl(certificate.link);
  const isExternal = /^https?:\/\//.test(linkUrl);

  const text = (
    <>
      {certificate.title && (
        <span className="block font-semibold">{certificate.title}</span>
      )}
      {certificate.description && (
        <span className="block">{certificate.description}</span>
      )}
    </>
  );

  return (
    <div className="flex items-start gap-4">
      {certificate.image?.filename ? (
        <button
          type="button"
          onClick={onOpen}
          aria-haspopup="dialog"
          aria-label={`${enlargeLabel}: ${certificate.title || ''}`}
          className="group relative shrink-0 bg-transparent border-0 p-0 cursor-zoom-in"
        >
          <CertificateImage image={certificate.image} title={certificate.title} />
          <span className="absolute inset-0 flex items-center justify-center text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="text-sm" />
          </span>
        </button>
      ) : (
        <CertificateImage />
      )}
      {linkUrl ? (
        <a
          href={linkUrl}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          className="no-underline text-inherit hover:underline"
        >
          {text}
        </a>
      ) : (
        <div>{text}</div>
      )}
    </div>
  );
}

function CertificateLightbox({ certificate, closeLabel, onClose }) {
  const closeButtonRef = React.useRef(null);

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.classList.add('overflow-hidden');
    closeButtonRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('overflow-hidden');
    };
  }, [onClose]);

  const { image, title } = certificate;
  const { filename } = image;
  const isSvg = filename.includes('.svg');

  // Portal to <body> so the overlay sits above the fixed site header
  return createPortal(
    // Escape is handled on document; clicking the backdrop closes the dialog
    // eslint-disable-next-line max-len
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title || getStoryblokAlt(image)}
      onClick={onClose}
      className="fixed inset-0 z-[999999] flex items-center justify-center bg-[rgba(0,0,0,0.4)] p-4 pt-12"
    >
      {/* eslint-disable-next-line max-len */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="relative w-full max-w-[400px]" onClick={(event) => event.stopPropagation()}>
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          className="absolute left-1/2 -top-5 -translate-x-1/2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-latori-green-dark text-white text-base border-0 cursor-pointer hover:opacity-90"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
        <img
          alt={getStoryblokAlt(image, title || '')}
          className="!m-0 block mx-auto max-h-[calc(100vh-6rem)] max-w-full w-auto h-auto rounded-3xl shadow-2xl"
          src={isSvg ? filename : `${filename}/m/800x0/filters:format(webp)`}
        />
      </div>
    </div>,
    document.body,
  );
}

function AuthorBio({ bio }) {
  if (!bio) return null;

  if (typeof bio === 'string') {
    return <p className="!my-0">{bio}</p>;
  }

  return render(bio, {
    markResolvers: {
      [MARK_LINK]: LinkResolver,
    },
    nodeResolvers: {
      // eslint-disable-next-line react/no-unstable-nested-components
      [NODE_PARAGRAPH]: (children) => (
        <p className="first-of-type:mt-0 last-of-type:mb-0">{children}</p>
      ),
    },
  });
}

// The reviewer box reuses the author story and layout; only the label and anchor differ.
// The author's own label (e.g. "Autorin") is ignored there since it describes authorship.
export default function BlogPostAuthorBox({ author, lang = 'de', role = 'author' }) {
  const [activeCertificate, setActiveCertificate] = React.useState(null);
  const closeLightbox = React.useCallback(() => setActiveCertificate(null), []);

  // The reviewer box only needs a resolved story with a name; bio/image are optional there.
  const canRender = role === 'reviewer'
    ? Boolean(author?.name && author?.content)
    : hasAuthorBox(author);
  if (!canRender) return null;

  const { name, content } = author;
  const labels = LABELS[lang] || LABELS.de;
  const linkedinUrl = getLinkUrl(content.linkedin);
  const certificates = Array.isArray(content.certificates) ? content.certificates : [];
  const isReviewer = role === 'reviewer';
  const roleLabel = isReviewer ? labels.reviewer : (content.label || labels.author);

  return (
    <aside
      id={isReviewer ? REVIEWER_BOX_ID : AUTHOR_BOX_ID}
      {...storyblokEditable(content)}
      className="not-prose scroll-mt-24 mt-12 border-2 border-solid border-latori-green-dark rounded p-6 md:p-8"
      aria-label={`${isReviewer ? labels.reviewer : labels.author}: ${name}`}
    >
      <div className="flex flex-col md:flex-row gap-6 md:gap-8">
        <AuthorImage image={content.image} name={name} />
        <div className="flex-1 min-w-0">
          <p className="font-monospace text-latori-green-dark text-sm !m-0 text-center md:text-left">
            {roleLabel}
          </p>
          <div className="flex items-center gap-3 mt-2">
            <p className="text-2xl font-semibold !m-0">{name}</p>
            {linkedinUrl && (
              <a
                href={linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${name} auf LinkedIn`}
                className="text-gray-700 hover:text-latori-green-dark"
              >
                <FontAwesomeIcon icon={faLinkedin} size="lg" />
              </a>
            )}
          </div>
          <div className="mt-4 leading-relaxed">
            <AuthorBio bio={content.bio} />
          </div>
          {certificates.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center gap-4">
                <FontAwesomeIcon
                  icon={faBadgeCheck}
                  size="lg"
                  className="w-9 text-latori-green-dark"
                />
                <p className="text-lg font-semibold !m-0">
                  {content.certificates_title || labels.certificates}
                </p>
              </div>
              <ul className="list-none !m-0 !pl-0 !pb-0 !pt-5 flex flex-col gap-5">
                {certificates.map((certificate) => (
                  <li
                    key={certificate._uid}
                    {...storyblokEditable(certificate)}
                    className="!p-0 !m-0"
                  >
                    <CertificateRow
                      certificate={certificate}
                      enlargeLabel={labels.enlarge}
                      onOpen={() => setActiveCertificate(certificate)}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      {activeCertificate && (
        <CertificateLightbox
          certificate={activeCertificate}
          closeLabel={labels.close}
          onClose={closeLightbox}
        />
      )}
    </aside>
  );
}
